/**
 * Construction du document d'apercu.
 *
 * L'apercu est rendu dans une iframe isolee plutot qu'injecte dans la page.
 * Cela garantit trois choses :
 *   - le CSS de l'eleve ne peut pas atteindre l'interface de la plateforme ;
 *   - le rendu n'herite d'aucun style de la plateforme, il est donc conforme a
 *     ce que produirait un vrai navigateur ;
 *   - le HTML de l'eleve s'execute dans un contexte cloisonne.
 */

import { HtmlCssWorkspace } from './types';

/**
 * Restrictions appliquees a l'iframe d'apercu.
 *
 * `allow-scripts` est volontairement absent : les exercices portent sur HTML et
 * CSS, et l'omettre empeche tout script injecte de s'executer. `allow-popups`
 * ne l'est pas non plus, pour qu'un apercu ne puisse pas ouvrir de fenetre.
 */
export const PREVIEW_SANDBOX = 'allow-same-origin';

/**
 * Feuille de style minimale appliquee avant le CSS de l'eleve.
 *
 * Elle se limite a donner un fond et une police de depart : tout ce que l'eleve
 * ecrit ensuite doit pouvoir la surcharger.
 */
const BASE_STYLES = `
  html, body { margin: 0; padding: 8px; }
  body {
    background: #ffffff;
    color: #000000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  }
  img { max-width: 100%; }
`;

/**
 * Assemble le document complet injecte dans l'iframe.
 *
 * Le CSS est place dans le `<head>` et le HTML dans le `<body>`, comme dans une
 * page reelle : un selecteur `body` ecrit par l'eleve se comporte donc comme
 * attendu, ce qui n'etait pas le cas avec l'injection dans un `<div>`.
 */
export const buildPreviewDocument = (workspace: HtmlCssWorkspace): string => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${BASE_STYLES}</style>
<style>
${workspace.css}
</style>
</head>
<body>
${workspace.html}
</body>
</html>`;
